class FyreCart extends HTMLElement {
  constructor() {
    super()
    this.form = this.querySelector('form')
    this.lastRemovedItem = null
    this.subtotalPrice = this.querySelector('.cart__subtotal-price')
    this.bindEvents()
  }

  bindEvents() {
    this.querySelectorAll('.cart-item__quantity-button').forEach((button) => {
      button.addEventListener('click', this.handleQuantityButtonClick.bind(this))
    })

    this.querySelectorAll('.cart-item__quantity-input').forEach((input) => {
      input.addEventListener('change', this.handleQuantityChange.bind(this))
    })

    this.querySelectorAll('.cart-item__remove').forEach((button) => {
      button.addEventListener('click', this.handleRemoveClick.bind(this))
    })
  }

  handleQuantityButtonClick(event) {
    const button = event.currentTarget
    const input = button.parentNode.querySelector('.cart-item__quantity-input')
    const action = button.dataset.action
    const currentValue = parseInt(input.value)

    if (action === 'increase') {
      input.value = currentValue + 1
    } else if (action === 'decrease' && currentValue > 0) {
      input.value = currentValue - 1
      // track removed item so continue shopping URL is correct
      if (parseInt(input.value) === 0) {
        const cartItem = button.closest('.cart-item')
        this.lastRemovedItem = {
          key: input.dataset.itemKey,
          quantity: 1,
          title: cartItem.querySelector('.cart-item__title')?.textContent?.trim() || '',
          sourceUrl: cartItem.dataset.sourceUrl || null,
        }
      }
    }

    this.updateItemQuantity(input.dataset.itemKey, parseInt(input.value))
  }

  handleQuantityChange(event) {
    const input = event.currentTarget
    const newQty = parseInt(input.value)
    // track removed item so continue shopping URL is correct
    if (newQty === 0) {
      const cartItem = input.closest('.cart-item')
      this.lastRemovedItem = {
        key: input.dataset.itemKey,
        quantity: parseInt(input.defaultValue) || 1,
        title: cartItem.querySelector('.cart-item__title')?.textContent?.trim() || '',
        sourceUrl: cartItem.dataset.sourceUrl || null,
      }
    }
    this.updateItemQuantity(input.dataset.itemKey, newQty)
  }

  handleRemoveClick(event) {
    const button = event.currentTarget
    const cartItem = button.closest('.cart-item')

    if (cartItem.hasAttribute('data-display-stand')) {
      this.removeDisplayStand()
      return
    }

    const itemKey = cartItem.dataset.itemKey
    const itemTitle = cartItem.querySelector('.cart-item__title').textContent
    const qtyInput = cartItem.querySelector('.cart-item__quantity-input')

    this.lastRemovedItem = {
      key: itemKey,
      quantity: qtyInput ? parseInt(qtyInput.value) : 1,
      title: itemTitle,
      sourceUrl: cartItem.dataset.sourceUrl || null,
    }

    this.updateItemQuantity(itemKey, 0)
    this.showNotification('Item removed', true)
  }

  async removeDisplayStand() {
    try {
      const cart = await fetch('/cart.js').then(r => r.json())

      const updates = {}
      cart.items.forEach(function(item) {
        const props = item.properties || {}
        if (props['_display_stand'] || props['_display_hitch']) {
          updates[item.key] = 0
        }
      })

      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates }),
      })

      if (!response.ok) throw new Error('Network response was not ok')

      const updatedCart = await response.json()
      this.updatePrices(updatedCart)
      this.updateCartCount(updatedCart)

      // get sourceUrl from the display stand bundle items (all share the same _source_url)
      const sourceUrl = this.querySelector('[data-display-stand][data-source-url], [data-display-hitch][data-source-url]')?.dataset.sourceUrl || null

      this.querySelectorAll('[data-display-stand], [data-display-hitch]').forEach(el => el.remove())

      if (updatedCart.item_count === 0) {
        if (sourceUrl) sessionStorage.setItem('bw_last_atc_url', sourceUrl)
        window.location.reload()
        return
      }

      this.showNotification('Display stand removed', false)
    } catch (error) {
      console.error('Error removing display stand:', error)
      this.showNotification('Error updating cart', false)
    }
  }

  /**
   * Updates .cart__shipping-price and "(Calculated at checkout)" when cart changes via JS.
   * NOTE: If $0.00 (hasMatch = true), show Calculated at checkout. If Free, hide Calculated at checkout.
   */
  updateShippingDisplay(cart) {
    const raw = this.dataset.shippingPrices || ''
    const skuList = raw.split(',').map((entry) => (entry.split('|')[0] || '').trim()).filter(Boolean)
    const hasMatch = cart.items && cart.items.some((item) => item.sku && skuList.includes(item.sku))

    const calculatedSpan = this.querySelector('[data-cart-shipping-calculated]')
    const priceEl = this.querySelector('[data-cart-shipping-display]')
    if (calculatedSpan) {
      calculatedSpan.style.display = hasMatch ? '' : 'none'
    }
    if (priceEl) {
      priceEl.textContent = hasMatch ? '$200.00' : 'Free'
    }
    return hasMatch
  }

  async updateItemQuantity(itemKey, quantity) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: itemKey,
          quantity: quantity,
        }),
      })

      if (!response.ok) throw new Error('Network response was not ok')

      const cart = await response.json()
      this.updatePrices(cart)
      // updateCartCount
      this.updateCartCount(cart) 

      // If quantity is 0, remove the item from DOM
      if (quantity === 0) {
        const itemToRemove = this.querySelector(`[data-item-key="${itemKey}"]`)
        // read sourceUrl before DOM removal or querySelector picks the wrong item
        const sourceUrl = (this.lastRemovedItem && this.lastRemovedItem.sourceUrl)
          || itemToRemove?.dataset.sourceUrl
          || null
        if (itemToRemove) {
          itemToRemove.remove()
        }
        if (cart.item_count === 0) {
          if (sourceUrl) sessionStorage.setItem('bw_last_atc_url', sourceUrl)
        window.location.reload()
          return
        }
      }
    } catch (error) {
      console.error('Error:', error)
      this.showNotification('Error updating cart', false)
    }
  }

  updatePrices(cart) {
    // Shipping match must run first so balance includes it
    const hasMatch = this.updateShippingDisplay(cart)
    const shippingCents = hasMatch ? 20000 : 0

    if (this.subtotalPrice) {
      this.subtotalPrice.textContent = this.formatMoney(cart.total_price)
    }

    // Balance = subtotal + shipping
    this.querySelectorAll('.cart__total .cart__total-price').forEach((el) => {
      el.textContent = this.formatMoney(cart.total_price + shippingCents)
    })

    // Update individual line items
    cart.items.forEach((item) => {
      const cartItem = this.querySelector(`[data-item-key="${item.key}"]`)
      if (cartItem) {
        // Display stand uses a custom calculated total — don't overwrite it
        if (cartItem.hasAttribute('data-display-stand')) return

        const linePrice = cartItem.querySelector('.cart-item__line-price-amount')
        const itemPrice = cartItem.querySelector('.cart-item__price')
        if (linePrice) {
          linePrice.textContent = this.formatMoney(item.final_line_price)
        }
        if (itemPrice) {
          itemPrice.textContent = this.formatMoney(item.final_price)
        }
      }
    })

    // Update cart discounts if they exist
    const discountElement = this.querySelector('.cart__discounts-amount')
    if (discountElement && cart.total_discount > 0) {
      discountElement.textContent = `-${this.formatMoney(cart.total_discount)}`
    }
  }

  formatMoney(cents) {
    return (cents / 100).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
    })
  }
  

  updateCartCount(cart = this.cart) {
    // updates the cart count in the header
    const cartCount = document.querySelector('.header__cart-count');
    if (cartCount) {
      cartCount.textContent = cart.item_count;
    }
  }

  showNotification(message, showUndo = false) {
    // Remove any existing notifications
    const existingNotification = document.querySelector('.cart-notification')
    if (existingNotification) {
      existingNotification.remove()
    }

    // Create notification element
    const notification = document.createElement('div')
    notification.className = 'cart-notification'

    const messageSpan = document.createElement('span')
    messageSpan.textContent = message
    notification.appendChild(messageSpan)

    if (showUndo && this.lastRemovedItem) {
      const undoButton = document.createElement('button')
      undoButton.className = 'cart-notification__undo'
      undoButton.textContent = 'Undo'
      undoButton.addEventListener('click', () => this.handleUndo())
      notification.appendChild(undoButton)
    }

    // Add notification to page
    document.body.appendChild(notification)

    // Remove notification after delay (unless user is hovering over it)
    let timeoutId
    const removeNotification = () => {
      notification.classList.add('cart-notification--hiding')
      setTimeout(() => notification.remove(), 300)
    }

    notification.addEventListener('mouseenter', () => clearTimeout(timeoutId))
    notification.addEventListener('mouseleave', () => {
      timeoutId = setTimeout(removeNotification, 3000)
    })

    timeoutId = setTimeout(removeNotification, 3000)
  }

  handleUndo() {
    if (this.lastRemovedItem) {
      this.updateItemQuantity(this.lastRemovedItem.key, this.lastRemovedItem.quantity)
      this.lastRemovedItem = null
      // Remove the notification immediately when undoing
      const notification = document.querySelector('.cart-notification')
      if (notification) notification.remove()
    }
  }
}

customElements.define('fyre-cart', FyreCart)
